/*
    Controller for Calendar Options Control Panel UI.
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import Assert from "tbAssert";

import TrackingCalendarOptionsServiceModule from "../../services/TrackingCalendarOptionsService";

const mScopeSvc = new WeakMap();
const mTrackingCalendarOptionsSvc = new WeakMap();

const mAdditionalPanelClasses = new WeakMap();
const mPanelIsOpen = new WeakMap();
const mPanelInitialized = new WeakMap();
const mApplyCalendarOptionsFn = new WeakMap();

const mCurrentCalendarOptions = new WeakMap();
const mCalendarViews = new WeakMap();

const mAgendaViewDaysModelOptions = new WeakMap();
const mCalendarViewListOptions = new WeakMap();

const mDirtyCheckWatchFuncs = new WeakMap();

const optionsPanelRootElementId = "tb-calendar-options-panel-root";

class CalendarOptionsPanelController {
    constructor($scope, TrackingCalendarOptions) {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert(TrackingCalendarOptions != null, "Assertion Failure: Missing Tracking Calendar Options Service Dependency");

        mScopeSvc.set(this, $scope);
        mTrackingCalendarOptionsSvc.set(this, TrackingCalendarOptions);

        mPanelInitialized.set(this, false);

        mTrackingCalendarOptionsSvc.get(this).loadPrefs();

        mCurrentCalendarOptions.set(this, {
            agendaViewDays: null,
            defaultCalendarView: null
        });

        mCalendarViews.set(this, mTrackingCalendarOptionsSvc.get(this).AvailableCalendarViews.map(
            (calendarViewPair) => {
                return {
                    viewName: calendarViewPair[0],
                    viewDisplayName: calendarViewPair[1]
                };
            }
        ));

        mAgendaViewDaysModelOptions.set(this, {
            updateOn: "default blur",
            debounce: {
                "default": 50,
                blur: 0
            }
        });

        mCalendarViewListOptions.set(this, {
            dataSource: mCalendarViews.get(this),
            dataTextField: "viewDisplayName",
            dataValueField: "viewName"
        });

        mDirtyCheckWatchFuncs.set(this, []);

        this._setWatches();

        mScopeSvc.get(this).$on(
            "$destroy",
            () => {
                let optionsPanelRootElement = angular.element(`#${optionsPanelRootElementId}`);
                kendo.destroy(optionsPanelRootElement);
            }
        );

    }

    get additionalPanelClasses() {
        return mAdditionalPanelClasses.get(this);
    }

    set additionalPanelClasses(updatedVal) {
        mAdditionalPanelClasses.set(this, updatedVal);
    }

    get panelIsOpen() {
        return mPanelIsOpen.get(this);
    }

    set panelIsOpen(updatedVal) {
        mPanelIsOpen.set(this, updatedVal);
    }

    get panelInitialized() {
        return mPanelInitialized.get(this);
    }

    set panelInitialized(updatedVal) {
        mPanelInitialized.set(this, updatedVal);
    }

    get applyCalendarOptions() {
        return mApplyCalendarOptionsFn.get(this);
    }

    set applyCalendarOptions(updatedFn) {
        mApplyCalendarOptionsFn.set(this, updatedFn);
    }

    get currentCalendarOptions() {
        return mCurrentCalendarOptions.get(this);
    }

    get agendaViewDaysModelOptions() {
        return mAgendaViewDaysModelOptions.get(this);
    }

    get calendarViewListOptions() {
        return mCalendarViewListOptions.get(this);
    }

    //Methods

    _clearWatches() {
        mDirtyCheckWatchFuncs.get(this).forEach((watchFunc) => {
            watchFunc();
        });
        mDirtyCheckWatchFuncs.get(this).length = 0;
    }

    _setWatches() {

        let defCalendarWatch = mScopeSvc.get(this).$watch(
            () => this.currentCalendarOptions.defaultCalendarView,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    if (this.calendarOptionsForm != null) {
                        this.calendarOptionsForm.$setDirty();
                        this._clearWatches();
                    }
                }
            }
        );

        mDirtyCheckWatchFuncs.get(this).push(defCalendarWatch);
    }

    loadCurrentCalendarOptions() {
        this.currentCalendarOptions.agendaViewDays = mTrackingCalendarOptionsSvc.get(this).AgendaDaysToShowPref;
        let defCalendarViewName = mTrackingCalendarOptionsSvc.get(this).DefaultCalendarView;

        this.currentCalendarOptions.defaultCalendarView = {
            viewName: defCalendarViewName,
            viewDisplayName: mTrackingCalendarOptionsSvc.get(this).GetCalendarViewDisplayName(defCalendarViewName)
        };
    }

    $onInit() {
        this.loadCurrentCalendarOptions();
    }

    applyCurrentOptions(e) {
        if (e != null) {
            e.preventDefault();
        }

        mTrackingCalendarOptionsSvc.get(this).AgendaDaysToShowPref = this.currentCalendarOptions.agendaViewDays;
        mTrackingCalendarOptionsSvc.get(this).DefaultCalendarView = this.currentCalendarOptions.defaultCalendarView.viewName;

        this.applyCalendarOptions();
    }

    cancelChanges(e) {
        if (e != null) {
            e.preventDefault();
        }

        this.loadCurrentCalendarOptions();
        this.calendarOptionsForm.$setPristine();
        this._setWatches();
    }

    hidePanel() {
        mPanelIsOpen.set(this, false);
    }
}

CalendarOptionsPanelController.$inject = [ "$scope", "TrackingCalendarOptions" ];

CalendarOptionsPanelController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiBootstrap,
    kendoDirectivesModule,
    TrackingCalendarOptionsServiceModule
];

CalendarOptionsPanelController.template = require("./CalendarOptionsPanel.tpl.html");

CalendarOptionsPanelController.controllerAs = "calendarOptionsPanel";

export default CalendarOptionsPanelController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-calendar-view/CalendarOptionsPanelController.js
// module id = oTh/
// module chunks = 1